import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Modal,
  Button,
  Accordion,
  Form,
  Alert,
  Badge,
  ProgressBar,
  Collapse,
} from "react-bootstrap";
import {
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaComment,
  FaShieldAlt,
  FaUser,
  FaBell,
  FaEye,
  FaClock,
  FaThumbsUp,
  FaThumbsDown,
  FaEnvelope,
  FaEnvelopeOpen,
  FaMobileAlt,
  FaHistory,
  FaFilter,
  FaSort,
} from "react-icons/fa";
import Header from "./Header";
import { getCachedData, setCachedData } from "../utils/apiCache";
import { ProductContext } from "../context/AppContext";
import Swal from "sweetalert2";

import { useSearchParams } from "react-router-dom";


const UserObservation = () => {
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const { userId } = useContext(ProductContext);
  const authtoken = localStorage.getItem("token");
  const [studentId, setStudentId] = useState(null);
  const [otpInput, setOtpInput] = useState("");
  const [showOtpFor, setShowOtpFor] = useState(null);
  const [acknowledgedIds, setAcknowledgedIds] = useState(new Set());
  const [acknowledging, setAcknowledging] = useState(false);
  const [filter, setFilter] = useState("all"); // all, acknowledged, pending
  const [sortBy, setSortBy] = useState("newest"); // newest, oldest
  const [showTiles, setShowTiles] = useState(true);
  const [showFilters, setShowFilters] = useState(true);

  const [searchParams] = useSearchParams();
  const notifiableType = searchParams.get("notifiable_type");
  const notifiableId = searchParams.get("notifiable_id");

  useEffect(() => {
    if (userId) fetchStudentId();
  }, [userId]);

  const fetchStudentId = async () => {
    try {
      const response = await fetch(
        `https://api.mirai.healthcare/api/student?regs_id=${userId}`,
        {
          headers: { "x-auth": authtoken },
        }
      );
      const data = await response.json();
      if (data.success && data.message.length > 0) {
        setStudentId(data.message[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching student ID:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) fetchUserObservations();
  }, [studentId]);

  const fetchUserObservations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://api.mirai.healthcare/api/student-details/user-observation?student_id=${studentId}`,
        { headers: { "x-auth": authtoken } }
      );
      const sortedObservations = (response.data.message || []).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setObservations(sortedObservations);
      // Track already acknowledged
      const acknowledged = sortedObservations
        .filter((obs) => obs.status === "Acknowledge")
        .map((obs) => obs.id);
      setAcknowledgedIds(new Set(acknowledged));
    } catch (error) {
      console.error("Error fetching observations:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load observations. Please try again.",
        icon: "error",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsViewed = async (observationId) => {
    // Optimistically update local state so UI updates instantly without any page refresh or spinner flicker
    setObservations((prevObs) =>
      prevObs.map((item) =>
        item.id === observationId
          ? { ...item, is_opened: true, opened_at: item.opened_at || new Date().toISOString() }
          : item
      )
    );

    try {
      await axios.patch(
        `https://api.mirai.healthcare/api/student-details/user-observation/isview/${observationId}`,
        {},
        { headers: { "x-auth": authtoken } }
      );
      console.log(`Marked observation ${observationId} as viewed.`);
    } catch (error) {
      console.error("Error updating view status:", error);
    }
  };

  const sendOtp = async () => {
    try {
      await axios.post(
        `https://api.mirai.healthcare/api/student-details/user-observation/send-otp`,
        {},
        { headers: { "x-auth": authtoken } }
      );
      Swal.fire({
        title: "OTP Sent!",
        text: "Check your registered mobile/email for the OTP.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error sending OTP:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to send OTP. Please try again.",
        icon: "error",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  const verifyOtp = async (observationId) => {
    if (!otpInput || otpInput.length !== 6) {
      Swal.fire({
        title: "Invalid OTP",
        text: "Please enter a valid 6-digit OTP.",
        icon: "warning",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    setAcknowledging(true);
    try {
      await axios.post(
        `https://api.mirai.healthcare/api/student-details/user-observation/verify-otp`,
        {
          otp: parseInt(otpInput),
          user_observ_id: observationId,
        },
        { headers: { "x-auth": authtoken } }
      );
      Swal.fire({
        title: "Acknowledged!",
        text: "Observation has been successfully acknowledged.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      setAcknowledgedIds((prev) => new Set([...prev, observationId]));
      setShowOtpFor(null);
      setOtpInput("");
      fetchUserObservations(); // Refresh list
    } catch (error) {
      console.error("OTP verification failed:", error);
      Swal.fire({
        title: "Invalid OTP",
        text: "Please check your OTP and try again.",
        icon: "error",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setAcknowledging(false);
    }
  };

  const toggleExpand = (id) => {
    markAsViewed(id);
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const isExpanded = (id) => expandedIds.has(id);
  const isAcknowledged = (id) => acknowledgedIds.has(id);

  // Filter and sort observations
  // const filteredAndSortedObservations = observations
  //   .filter((obs) => {
  //     if (filter === "acknowledged") return isAcknowledged(obs.id);
  //     if (filter === "pending") return !isAcknowledged(obs.id);
  //     return true;
  //   })
  //   .sort((a, b) => {
  //     if (sortBy === "newest")
  //       return new Date(b.created_at) - new Date(a.created_at);
  //     if (sortBy === "oldest")
  //       return new Date(a.created_at) - new Date(b.created_at);
  //     return 0;
  //   });

   const [createdBy, setCreatedBy] = useState("");
const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");

const [createdByList, setCreatedByList] = useState([]);


// applied filters (real filter)
const [appliedFilters, setAppliedFilters] = useState({
  createdBy: "",
  fromDate: "",
  toDate: "",
});

useEffect(() => {
  if (observations.length > 0) {
    const uniqueUsers = [
      ...new Set(
        observations
          .map((obs) => obs.user?.full_name)
          .filter(Boolean)
      ),
    ];
    setCreatedByList(uniqueUsers);
  }
}, [observations]);

  const filteredAndSortedObservations = observations
  .filter((obs) => {
    if (filter === "acknowledged" && !isAcknowledged(obs.id)) return false;
    if (filter === "pending" && (obs.is_opened || isAcknowledged(obs.id))) return false;
    if (filter === "opened" && !obs.is_opened) return false;

    // applied filters
    if (
      appliedFilters.createdBy &&
      obs.user?.full_name !== appliedFilters.createdBy
    )
      return false;

    if (
      appliedFilters.fromDate &&
      new Date(obs.created_at) < new Date(appliedFilters.fromDate)
    )
      return false;

    if (
      appliedFilters.toDate &&
      new Date(obs.created_at) > new Date(appliedFilters.toDate)
    )
      return false;

    return true;
  })
  .sort((a, b) => {
    if (sortBy === "newest")
      return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === "oldest")
      return new Date(a.created_at) - new Date(b.created_at);
    return 0;
  });

  const getStats = () => {
    const total = observations.length;
    const opened = observations.filter((obs) => obs.is_opened === true && !isAcknowledged(obs.id)).length;
    const acknowledged = observations.filter((obs) =>
      isAcknowledged(obs.id)
    ).length;
    const pending = observations.filter((obs) => !obs.is_opened && !isAcknowledged(obs.id)).length;
    return { total, opened, acknowledged, pending };
  };

  const handleApplyFilter = () => {
  setAppliedFilters({
    createdBy,
    fromDate,
    toDate,
  });
};

const handleResetFilter = () => {
  setCreatedBy("");
  setFromDate("");
  setToDate("");

  setAppliedFilters({
    createdBy: "",
    fromDate: "",
    toDate: "",
  });
};

useEffect(() => {
  if (notifiableId && filteredAndSortedObservations.length > 0) {
    const timer = setTimeout(() => {
      const element = document.getElementById(`obs-${notifiableId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 400); // 400ms delay taaki layout render complete ho jaye

    return () => clearTimeout(timer);
  }
}, [notifiableId, filteredAndSortedObservations]);


const capitalizeWords = (str) => {
  return str
    ?.toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
 

  const stats = getStats();

  if (loading) {
    return (
      <div className="page-wrapper min-vh-100 bg-light">
        <Header />
        <Container
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "50vh" }}
        >
          <div className="text-center">
            <Spinner
              animation="border"
              variant="primary"
              size="lg"
              className="mb-3"
            />
            <p className="text-muted">Loading your observations...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-wrapper min-vh-100">
      <Header />

      {/* Header Section */}
      <div className="bg-light border-bottom py-3 mt-3">
        <Container fluid>
          <Row className="align-items-center justify-content-between">
            <Col className="d-flex align-items-center gap-2">
              {/* <FaBell className="text-primary fs-4" /> */}
              <div>
                <h2
                  className="page-title fw-bold mb-0"
                  style={{ letterSpacing: "0.5px" }}
                >
                  Student Observations
                </h2>
                <small className="text-muted">
                  Review and acknowledge behavioral feedback
                </small>
              </div>
            </Col>

            <Col xs="auto">
              <Badge bg="primary" className="px-3 py-2">
                {stats.total} Total
              </Badge>
            </Col>
          </Row>
        </Container>
      </div>

      <Container fluid className="py-4">
        {/* 1. Stat Tiles Section with Independent Collapse */}
        <div className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom user-select-none">
          <div 
            className="d-flex align-items-center gap-2 cursor-pointer"
            onClick={() => setShowTiles(!showTiles)}
          >
            <FaBell className="text-primary fs-14" />
            <span className="fw-bold text-dark fs-14">Statistics Overview</span>
          </div>
          <Button
            variant="light"
            size="sm"
            className="border rounded-pill px-3 py-1 text-secondary fs-12 fw-semibold d-inline-flex align-items-center gap-1.5 shadow-2xs"
            onClick={() => setShowTiles(!showTiles)}
          >
            <span>{showTiles ? "Hide Tiles" : "Show Tiles"}</span>
            {showTiles ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
          </Button>
        </div>

        <Collapse in={showTiles}>
          <div className="mb-3">
            {/* Statistics Cards (4 Simple Tiles) */}
            <Row className="g-2 g-md-3">
              {/* Tile 1: Total Observations */}
              <Col xs={6} md={3}>
                <Card 
                  className="border rounded-3 h-100 shadow-2xs cursor-pointer transition-all hover-lift"
                  style={{ backgroundColor: '#ffffff', borderColor: filter === 'all' ? '#3b82f6' : '#e2e8f0' }}
                  onClick={() => setFilter('all')}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-secondary fs-12 fw-semibold">Total</span>
                      <div className="p-2 rounded-3 bg-primary-subtle text-primary d-inline-flex">
                        <FaBell className="fs-14" />
                      </div>
                    </div>
                    <h3 className="fw-bold text-dark mb-0 fs-20">{stats.total}</h3>
                    <small className="text-muted fs-11">Total Observations</small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Tile 2: Open */}
              <Col xs={6} md={3}>
                <Card 
                  className="border rounded-3 h-100 shadow-2xs cursor-pointer transition-all hover-lift"
                  style={{ backgroundColor: '#ffffff', borderColor: filter === 'opened' ? '#0284c7' : '#e2e8f0' }}
                  onClick={() => setFilter('opened')}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-secondary fs-12 fw-semibold">Open</span>
                      <div className="p-2 rounded-3 bg-info-subtle text-info d-inline-flex">
                        <FaEnvelopeOpen className="fs-14" />
                      </div>
                    </div>
                    <h3 className="fw-bold text-dark mb-0 fs-20">{stats.opened}</h3>
                    <small className="text-muted fs-11">Opened</small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Tile 3: Acknowledged */}
              <Col xs={6} md={3}>
                <Card 
                  className="border rounded-3 h-100 shadow-2xs cursor-pointer transition-all hover-lift"
                  style={{ backgroundColor: '#ffffff', borderColor: filter === 'acknowledged' ? '#10b981' : '#e2e8f0' }}
                  onClick={() => setFilter('acknowledged')}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-secondary fs-12 fw-semibold">Acknowledged</span>
                      <div className="p-2 rounded-3 bg-success-subtle text-success d-inline-flex">
                        <FaCheckCircle className="fs-14" />
                      </div>
                    </div>
                    <h3 className="fw-bold text-dark mb-0 fs-20">{stats.acknowledged}</h3>
                    <small className="text-muted fs-11">Acknowledged</small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Tile 4: Pending Review */}
              <Col xs={6} md={3}>
                <Card 
                  className="border rounded-3 h-100 shadow-2xs cursor-pointer transition-all hover-lift"
                  style={{ backgroundColor: '#ffffff', borderColor: filter === 'pending' ? '#f59e0b' : '#e2e8f0' }}
                  onClick={() => setFilter('pending')}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-secondary fs-12 fw-semibold">Pending</span>
                      <div className="p-2 rounded-3 bg-warning-subtle text-warning d-inline-flex">
                        <FaClock className="fs-14" />
                      </div>
                    </div>
                    <h3 className="fw-bold text-dark mb-0 fs-20">{stats.pending}</h3>
                    <small className="text-muted fs-11">Pending Review</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Collapse>

        {/* 2. Filters Section with Independent Collapse */}
        <div className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom user-select-none mt-3">
          <div 
            className="d-flex align-items-center gap-2 cursor-pointer"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter className="text-primary fs-14" />
            <span className="fw-bold text-dark fs-14">Filters & Sorting</span>
          </div>
          <Button
            variant="light"
            size="sm"
            className="border rounded-pill px-3 py-1 text-secondary fs-12 fw-semibold d-inline-flex align-items-center gap-1.5 shadow-2xs"
            onClick={() => setShowFilters(!showFilters)}
          >
            <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
            {showFilters ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
          </Button>
        </div>

        <Collapse in={showFilters}>
          <div>
            {/* Posted By & Date Filters */}
            <Row className="g-3 mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Posted By</Form.Label>
                  <Form.Select
                    value={createdBy}
                    onChange={(e) => setCreatedBy(e.target.value)}
                  >
                    <option value="">All</option>
                    {createdByList.map((name, i) => (
                      <option key={i} value={name}>
                        {capitalizeWords(name)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>From Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>To Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={3} className="d-flex align-items-end gap-2">
                <Button variant="primary" onClick={handleApplyFilter}>
                  Apply
                </Button>
                <Button variant="secondary" onClick={handleResetFilter}>
                  Reset
                </Button>
              </Col>
            </Row>

            {/* Progress Bar */}
            {stats.total > 0 && (
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body className="py-2.5">
                  <div className="d-flex justify-content-between align-items-center mb-1.5">
                    <span className="fw-semibold fs-13">Acknowledgment Progress</span>
                    <span className="text-muted small fs-12">
                      {stats.acknowledged} of {stats.total} completed
                    </span>
                  </div>
                  <ProgressBar
                    now={(stats.acknowledged / stats.total) * 100}
                    variant="success"
                    style={{ height: "6px" }}
                  />
                </Card.Body>
              </Card>
            )}

            {/* Filters and Sort Card */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="py-3">
                <Row className="g-3 align-items-center">
                  <Col md={6}>
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-semibold d-flex align-items-center gap-2">
                        <FaFilter className="text-primary" />
                        Filter:
                      </span>
                      <div className="btn-group flex-wrap" role="group">
                        <Button
                          variant={filter === "all" ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => setFilter("all")}
                        >
                          All
                        </Button>
                        <Button
                          variant={filter === "opened" ? "info" : "outline-info"}
                          size="sm"
                          onClick={() => setFilter("opened")}
                        >
                          Open ({stats.opened})
                        </Button>
                        <Button
                          variant={
                            filter === "pending" ? "warning" : "outline-warning"
                          }
                          size="sm"
                          onClick={() => setFilter("pending")}
                        >
                          Pending
                        </Button>
                        <Button
                          variant={
                            filter === "acknowledged"
                              ? "success"
                              : "outline-success"
                          }
                          size="sm"
                          onClick={() => setFilter("acknowledged")}
                        >
                          Acknowledged
                        </Button>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-center gap-3 justify-content-md-end">
                      <span className="fw-semibold d-flex align-items-center gap-2">
                        <FaSort className="text-primary" />
                        Sort:
                      </span>
                      <div className="btn-group" role="group">
                        <Button
                          variant={
                            sortBy === "newest" ? "primary" : "outline-primary"
                          }
                          size="sm"
                          onClick={() => setSortBy("newest")}
                        >
                          Newest First
                        </Button>
                        <Button
                          variant={
                            sortBy === "oldest" ? "primary" : "outline-primary"
                          }
                          size="sm"
                          onClick={() => setSortBy("oldest")}
                        >
                          Oldest First
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        </Collapse>

        {/* Observations List */}
        {filteredAndSortedObservations?.length > 0 ? (
          <Row className="g-4">
            {filteredAndSortedObservations?.map((obs) => {
  const isAck = isAcknowledged(obs.id);
  const isOpen = obs.is_opened === true;
  const themeColor = isAck ? "success" : isOpen ? "info" : "warning";
  const isHighlighted = Number(notifiableId) === Number(obs?.id);

  return (
    <Col 
      xs={12} 
      lg={6} 
      key={obs.id} 
      id={`obs-${obs.id}`} // Unique DOM ID scroll & selection ke liye
      className="d-flex"
    >
      <Card
  className={`w-100 shadow-sm border-0 h-100 transition-all hover-lift border-${themeColor} border-2`}
  style={{
    borderRadius: "12px",
    borderColor: isHighlighted ? "#6366f1" : undefined,
    borderWidth: isHighlighted ? "3px" : undefined,
    borderStyle: isHighlighted ? "solid" : undefined,
    background: isHighlighted 
      ? "linear-gradient(145deg, #eef2ff, #e0e7ff)" 
      : "linear-gradient(145deg, #ffffff, #f8f9fa)",
    boxShadow: isHighlighted ? "0 0 20px rgba(99, 102, 241, 0.4)" : undefined,
    transform: isHighlighted ? "scale(1.02)" : "none",
    transition: "all 0.3s ease-in-out"
  }}
>
        {/* Card Header */}
        <Card.Header
          className={`border-0 py-3 bg-${themeColor} bg-opacity-10`}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <div
                className={`p-2 rounded-circle bg-${themeColor} bg-opacity-20`}
              >
                <FaUser
                  className={`text-${themeColor}`}
                />
              </div>
              <div>
                <h6 className="fw-bold mb-0 fs-14 d-flex align-items-center gap-2">
                  {capitalizeWords(obs.student?.student_full_name)}
                  {isHighlighted && (
                    <Badge bg="warning" className="fs-12 text-white">
                      Highlighted
                    </Badge>
                  )}
                </h6>
                <small className="text-muted d-flex flex-wrap align-items-center gap-1 fs-12">
                  <FaCalendarAlt size={12} />
                  {new Date(obs.created_at).toLocaleDateString(
                    "en-IN",
                    {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    }
                  )}
                  {obs.user?.full_name && (
                    <span
                      className="ms-1 text-primary fw-semibold"
                      style={{ fontSize: "0.75rem" }}
                    >
                      • Posted by: {capitalizeWords(obs?.user?.full_name)}
                    </span>
                  )}
                </small>
              </div>
            </div>

            {/* Status Badge Container */}
            <div className="d-flex align-items-center gap-1.5 flex-wrap ms-auto ms-sm-0">
              {isAck ? (
                <Badge
                  bg="success"
                  className="d-inline-flex align-items-center gap-1 px-2.5 py-1.5 fs-11"
                >
                  <FaCheckCircle className="fs-11" /> Acknowledged
                </Badge>
              ) : isOpen ? (
                <Badge
                  bg="info"
                  className="d-inline-flex align-items-center gap-1 px-2.5 py-1.5 fs-11"
                >
                  <FaEnvelopeOpen className="fs-11" /> Opened
                </Badge>
              ) : (
                <Badge
                  bg="warning"
                  className="d-inline-flex align-items-center gap-1 px-2.5 py-1.5 fs-11"
                >
                  <FaClock className="fs-11" /> Pending
                </Badge>
              )}
            </div>
          </div>
        </Card.Header>

        {/* Card Body */}
        <Card.Body className="p-0">
          {/* Expandable Header */}
          <div
            className="p-3 border-bottom cursor-pointer"
            onClick={() => toggleExpand(obs.id)}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                {obs.observation?.is_positive === true ? (
                  <FaThumbsUp className="text-success fs-5" />
                ) : (
                  <FaThumbsDown className="text-danger fs-5" />
                )}
                <div>
                  <h6 className="fw-bold mb-1">
                    {obs.observation?.title || "Observation"}
                  </h6>
                  <p
                    className="text-muted small mb-0 text-truncate"
                    style={{ maxWidth: "300px" }}
                  >
                    {obs.rmk ||
                      obs.observation?.rmk ||
                      "No remarks provided"}
                  </p>
                </div>
              </div>
              <Button
                variant="link"
                className="p-0 text-decoration-none"
              >
                {isExpanded(obs.id) ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}
              </Button>
            </div>
          </div>

          {/* Expandable Content */}
          {isExpanded(obs.id) && (
            <div className="p-3 bg-light bg-opacity-50">
              {/* Behavior Details */}
              <div className="mb-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <FaComment className="text-primary" />
                  <h6 className="fw-bold mb-0">Behavior Details</h6>
                </div>
                <Card className="border-0 bg-white">
                  <Card.Body>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Badge
                        bg={
                          obs.observation?.is_positive === true
                            ? "success"
                            : "danger"
                        }
                        className="d-flex align-items-center gap-1 px-3 py-2"
                      >
                        {obs.observation?.is_positive === true ? (
                          <FaThumbsUp />
                        ) : (
                          <FaThumbsDown />
                        )}
                        {obs.observation?.is_positive === true
                          ? "Good Behavior"
                          : "Needs Improvement"}
                      </Badge>
                    </div>
                    {obs.is_opened && obs.opened_at && (
                      <div className="mb-2 text-info fs-12 d-flex align-items-center gap-1">
                        <FaEnvelopeOpen size={12} />
                        <span>
                          Opened on: {new Date(obs.opened_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                      </div>
                    )}
                    {obs.observation?.rmk && (
                      <p className="mb-2 text-muted">
                        <strong>General Note:</strong>{" "}
                        {obs.observation.rmk}
                      </p>
                    )}
                    {obs.rmk && (
                      <p className="mb-0 text-muted">
                        <strong>Specific Remark:</strong> {obs.rmk}
                      </p>
                    )}
                    {obs.admin_reason && (
                      <Alert
                        variant="info"
                        className="mt-2 mb-0 small"
                      >
                        <strong>Admin Note:</strong>{" "}
                        {obs.admin_reason}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </div>

              {/* Acknowledgment Section */}
              <div className="mt-3">
                {isAcknowledged(obs.id) ? (
                  <Alert
                    variant="success"
                    className="border-0 d-flex align-items-center gap-2 mb-0"
                  >
                    <FaCheckCircle className="fs-5" />
                    <div>
                      <strong>Acknowledged</strong>
                      <div className="small text-muted">
                        You have successfully acknowledged this observation
                      </div>
                    </div>
                  </Alert>
                ) : showOtpFor === obs.id ? (
                  <Card className="border-0 bg-info bg-opacity-10">
                    <Card.Body>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <FaShieldAlt className="text-info fs-5" />
                        <h6 className="fw-bold mb-0">
                          Verify with OTP
                        </h6>
                      </div>

                      <Alert
                        variant="info"
                        className="border-0 bg-info bg-opacity-25 mb-3"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <FaMobileAlt />
                          <FaEnvelope />
                          <span className="small">
                            OTP sent to your registered mobile/email
                          </span>
                        </div>
                      </Alert>

                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          Enter 6-digit OTP
                        </Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="000000"
                          value={otpInput}
                          onChange={(e) =>
                            setOtpInput(e.target.value.slice(0, 6))
                          }
                          maxLength={6}
                          className="text-center fs-4 fw-bold py-2"
                          style={{ letterSpacing: "8px" }}
                        />
                      </Form.Group>

                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          className="flex-fill"
                          onClick={() => {
                            setShowOtpFor(null);
                            setOtpInput("");
                          }}
                          disabled={acknowledging}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          className="flex-fill d-flex align-items-center justify-content-center gap-2"
                          onClick={() => verifyOtp(obs.id)}
                          disabled={
                            acknowledging || otpInput.length !== 6
                          }
                        >
                          {acknowledging ? (
                            <Spinner size="sm" />
                          ) : (
                            <FaShieldAlt />
                          )}
                          Verify OTP
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ) : (
                  <Button
                    variant="primary"
                    className="w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                    onClick={() => {
                      sendOtp();
                      setShowOtpFor(obs.id);
                    }}
                  >
                    <FaShieldAlt />
                    Acknowledge Observation
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
})}
          </Row>
        ) : (
          <Card className="border-0 shadow-sm text-center py-5">
            <Card.Body>
              <FaBell className="text-muted fs-1 mb-3" />
              <h5 className="text-muted">
                {filter === "all"
                  ? "No observations found"
                  : `No ${filter} observations`}
              </h5>
              <p className="text-muted mb-4">
                {filter === "all"
                  ? "You don't have any observations yet."
                  : `You don't have any ${filter} observations.`}
              </p>
              {filter !== "all" && (
                <Button
                  variant="outline-primary"
                  onClick={() => setFilter("all")}
                  className="d-inline-flex align-items-center gap-2"
                >
                  <FaHistory />
                  Show All Observations
                </Button>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* Custom Styles */}
      <style jsx>{`
        .hover-lift:hover {
          transform: translateY(-2px);
          transition: all 0.3s ease;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .transition-all {
          transition: all 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default UserObservation;
